'use client'

// Paid promotion (MOCK — no billing) for My-listings. User-selected benefit
// set (2026-07-15): deck front slot · highlighted price-strip dot · freshness
// bump every 3 days · automatic A/B cover+title testing · opt-in social-proof
// stats. Each benefit row carries a miniature "how it looks" preview so the
// poster sees exactly what they buy. Active state manages the bump cooldown
// (functional in the demo store) and shows mock A/B + social numbers.
import * as React from 'react'
import {
  BadgeCheck,
  Flame,
  FlaskConical,
  Layers,
  LineChart,
  RefreshCw,
  Rocket,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { RModal, pillBtnBase, useRToast } from '@/components/roostr'
import { useI18n } from '@/lib/i18n'
import { useMyListingsStore } from './my-listings-store'
import type { MyListing } from './post-data'

const DAY_MS = 24 * 60 * 60 * 1000
const BUMP_COOLDOWN_MS = 3 * DAY_MS
// One purchase = one week. No auto-renew: the promotion simply lapses when
// promoUntil passes (user decision 2026-07-15).
const PROMO_DURATION_MS = 7 * DAY_MS

/** Promotion is time-boxed — active only until its paid week runs out. */
export function isPromoActive(l: MyListing): boolean {
  return (l.promoUntil ?? 0) > Date.now()
}

// Deterministic mock stats derived from real saves so numbers stay stable.
export function promoStats(l: MyListing) {
  return { shortlisted: Math.max(3, l.saves), viewings: Math.max(2, Math.round(l.saves * 0.4)) }
}

// ── Miniature previews ("this is how it looks") ─────────────────────────────

function DeckPreview() {
  return (
    <div className="relative h-[64px] w-[104px] shrink-0">
      {[2, 1, 0].map((d) => (
        <div
          key={d}
          className={cn(
            'absolute overflow-hidden rounded-[8px] border bg-surface',
            d === 0 ? 'border-primary shadow-e2' : 'border-outline-variant'
          )}
          style={{ left: d * 14, top: d * 5, width: 64, height: 52 - d * 6, zIndex: 3 - d }}
        >
          <div
            className="h-[26px] w-full"
            style={{ background: `linear-gradient(120deg, #EADCCA, ${d === 0 ? '#BE9472' : '#D8C2A8'})` }}
          />
          {d === 0 ? (
            <span className="absolute left-1 top-1 rounded-full bg-primary px-[4px] py-[1px] text-[6.5px] font-extrabold tracking-[0.4px] text-white">
              PROMO
            </span>
          ) : null}
          <div className="mx-1 mt-[4px] h-[4px] rounded bg-surface-high" />
          <div className="mx-1 mt-[3px] h-[4px] w-2/3 rounded bg-surface-high" />
        </div>
      ))}
    </div>
  )
}

function StripPreview() {
  const dots = [8, 22, 38, 68, 82, 92]
  return (
    <div className="relative h-[38px] w-[104px] shrink-0">
      <div className="absolute left-0 right-0 top-[24px] h-[3px] rounded bg-surface-high" />
      {dots.map((x) => (
        <span
          key={x}
          className="absolute top-[22.5px] h-[6px] w-[6px] rounded-full"
          style={{ left: `${x}%`, background: 'color-mix(in srgb, rgb(var(--primary)) 30%, rgb(var(--surface-high)))' }}
        />
      ))}
      {/* The promoted dot — coral, ringed, with its mini-card peeking open */}
      <span className="absolute left-[52%] top-[20px] h-[11px] w-[11px] rounded-full bg-primary shadow-[0_0_0_2px_rgb(var(--surface)),0_0_0_4px_color-mix(in_srgb,rgb(var(--primary))_35%,transparent)]" />
      <div className="absolute left-[38%] top-0 rounded-[5px] border border-outline-variant bg-surface px-[5px] py-[2px] text-[7px] font-bold shadow-e1">
        <span className="text-primary">●</span> 23 500 Kč
      </div>
    </div>
  )
}

function BumpPreview({ t }: { t: (k: string, f?: string) => string }) {
  return (
    <div className="flex w-[104px] shrink-0 flex-col items-center gap-[5px]">
      <span className="text-[9.5px] text-ink-muted line-through">{t('myListings.promoBumpOld', '24 days ago')}</span>
      <RefreshCw size={11} className="text-primary" />
      <span className="rounded-full bg-success px-[7px] py-[2px] text-[8.5px] font-extrabold text-white">
        {t('myListings.promoBumpNew', 'JUST NOW')}
      </span>
    </div>
  )
}

function AbPreview() {
  return (
    <div className="flex w-[104px] shrink-0 items-end justify-center gap-2">
      {(
        [
          ['A', '4.1%', false],
          ['B', '6.3%', true],
        ] as const
      ).map(([label, ctr, winner]) => (
        <div key={label} className="flex flex-col items-center gap-[3px]">
          <div
            className={cn(
              'h-[34px] w-[40px] overflow-hidden rounded-[6px] border',
              winner ? 'border-success' : 'border-outline-variant opacity-70'
            )}
            style={{
              background: winner
                ? 'linear-gradient(120deg, #EADCCA, #BE9472)'
                : 'linear-gradient(200deg, #E0D4C4, #C9B08E)',
            }}
          />
          <span className={cn('font-mono text-[8.5px] font-bold', winner ? 'text-success' : 'text-ink-muted')}>
            {label} · {ctr}
            {winner ? ' ✓' : ''}
          </span>
        </div>
      ))}
    </div>
  )
}

function SocialPreview({ l, t }: { l: MyListing; t: (k: string, f?: string) => string }) {
  const s = promoStats(l)
  return (
    <div className="flex w-[104px] shrink-0 items-center justify-center">
      <span className="inline-flex items-center gap-1 rounded-full bg-primary-container px-[7px] py-[3px] text-[7.5px] font-bold leading-[1.3] text-on-primary-container">
        <Flame size={9} className="shrink-0" />
        {s.shortlisted} {t('myListings.promoSocialShort', 'shortlisted')} · {s.viewings}{' '}
        {t('myListings.promoSocialViewings', 'viewing requests')}
      </span>
    </div>
  )
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function PromoteModal({ l, open, onClose }: { l: MyListing; open: boolean; onClose: () => void }) {
  const { t } = useI18n()
  const { show } = useRToast()
  const updateMyListing = useMyListingsStore((s) => s.updateMyListing)

  // Cooldown/expiry tick only while the modal is open; Date.now in
  // handlers/render of a client-gated component is fine (mock).
  const now = Date.now()
  const promoted = isPromoActive(l)
  const promoDaysLeft = Math.max(1, Math.ceil(((l.promoUntil ?? 0) - now) / DAY_MS))
  const bumpReadyAt = (l.promoLastBump ?? 0) + BUMP_COOLDOWN_MS
  const bumpReady = now >= bumpReadyAt
  const daysLeft = Math.max(1, Math.ceil((bumpReadyAt - now) / DAY_MS))

  const activate = () => {
    const prev = l.promoUntil
    updateMyListing(l.id, { promoUntil: Date.now() + PROMO_DURATION_MS })
    show({
      msg: t(
        'myListings.promoActivatedToast',
        'Promoted for 7 days — stops automatically, no auto-renew (demo, no charge)'
      ),
      undo: () => updateMyListing(l.id, { promoUntil: prev ?? 0 }),
    })
  }
  const deactivate = () => {
    updateMyListing(l.id, { promoUntil: 0 })
    show({ msg: t('myListings.promoEndedToast', 'Promotion ended') })
    onClose()
  }
  const bump = () => {
    if (!bumpReady) return
    updateMyListing(l.id, { createdLabel: 'just now', promoLastBump: Date.now(), fresh: true })
    show({ msg: t('myListings.promoBumpedToast', 'Freshness bumped — your listing sorts like new again') })
  }

  const stats = promoStats(l)
  const benefits: Array<{
    icon: React.ElementType
    title: string
    desc: string
    preview: React.ReactNode
    activeNote?: React.ReactNode
  }> = [
    {
      icon: Layers,
      title: t('myListings.promoDeckTitle', 'Front of the swipe deck'),
      desc: t(
        'myListings.promoDeckDesc',
        'A guaranteed slot among the first cards of the daily deck for every seeker whose search matches.'
      ),
      preview: <DeckPreview />,
      activeNote: t('myListings.promoDeckActive', 'Live — showing in first 3 cards of matching decks'),
    },
    {
      icon: LineChart,
      title: t('myListings.promoStripTitle', 'Highlighted dot on the price strip'),
      desc: t(
        'myListings.promoStripDesc',
        'Your listing stands out inside the search page’s price chart — coral, ringed, mini-card ready.'
      ),
      preview: <StripPreview />,
      activeNote: t('myListings.promoStripActive', 'Live on every matching search'),
    },
    {
      icon: RefreshCw,
      title: t('myListings.promoBumpTitle', 'Freshness bump — every 3 days'),
      desc: t(
        'myListings.promoBumpDesc',
        'One tap re-sorts your listing like it was posted today. No deleting, no reposting, saves stay.'
      ),
      preview: <BumpPreview t={t} />,
      activeNote: promoted ? (
        <button
          type="button"
          onClick={bump}
          disabled={!bumpReady}
          className={cn(
            pillBtnBase,
            'px-3 py-[5px] text-[11.5px]',
            bumpReady
              ? 'bg-primary-container text-on-primary-container'
              : 'cursor-default bg-surface-high text-ink-muted'
          )}
        >
          <RefreshCw size={12} />
          {bumpReady
            ? t('myListings.promoBumpNow', 'Bump now')
            : `${t('myListings.promoBumpIn', 'Next bump in')} ${daysLeft} ${t('myListings.promoBumpDays', 'd')}`}
        </button>
      ) : undefined,
    },
    {
      icon: FlaskConical,
      title: t('myListings.promoAbTitle', 'A/B cover & title testing'),
      desc: t(
        'myListings.promoAbDesc',
        'We rotate your cover photo and title automatically, measure clicks and lock in the winner.'
      ),
      preview: <AbPreview />,
      activeNote: t('myListings.promoAbActive', 'Testing — variant B leads (6.3% vs 4.1% CTR)'),
    },
    {
      icon: Flame,
      title: t('myListings.promoSocialTitle', 'Social proof on the card'),
      desc: t(
        'myListings.promoSocialDesc',
        'Real demand numbers on your card — scarcity moves renters faster than any badge. Opt-in, never invented.'
      ),
      preview: <SocialPreview l={l} t={t} />,
      activeNote: `${t('myListings.promoSocialActive', 'Live —')} ${stats.shortlisted} ${t(
        'myListings.promoSocialShort',
        'shortlisted'
      )} · ${stats.viewings} ${t('myListings.promoSocialViewings', 'viewing requests')}`,
    },
  ]

  return (
    <RModal open={open} onClose={onClose} width={560} mobileSheet ariaLabel={t('myListings.promoTitle', 'Promote this listing')}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-primary-container">
          <Rocket size={20} className="text-on-primary-container" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[16.5px] font-bold tracking-[-0.2px]">
              {t('myListings.promoTitle', 'Promote this listing')}
            </span>
            {promoted ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success px-[8px] py-[2px] text-[9.5px] font-extrabold tracking-[0.4px] text-white">
                <BadgeCheck size={11} /> {t('myListings.promoActive', 'ACTIVE')} · {promoDaysLeft}{' '}
                {t('myListings.promoBumpDays', 'd')}
              </span>
            ) : null}
          </div>
          <div className="mt-px text-[12px] text-ink-muted">
            {t(
              'myListings.promoSubtitle',
              'Five boosts for one week, one payment. Stops automatically — no auto-renew. This demo charges nothing.'
            )}
          </div>
        </div>
      </div>

      <div className="mt-3.5 flex max-h-[54vh] flex-col gap-2 overflow-y-auto pr-1">
        {benefits.map(({ icon: Icon, title, desc, preview, activeNote }) => (
          <div key={title} className="flex items-center gap-3 rounded-shape-sm bg-surface-low px-3 py-[10px]">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-[6px]">
                <Icon size={14} className="shrink-0 text-primary" />
                <span className="text-[13px] font-bold">{title}</span>
              </div>
              <p className="mt-[3px] text-[11.5px] leading-[1.5] text-ink-secondary">{desc}</p>
              {promoted && activeNote ? (
                <div className="mt-[6px] text-[11px] font-semibold text-success">{activeNote}</div>
              ) : null}
            </div>
            {preview}
          </div>
        ))}
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-3 border-t border-outline-variant pt-3">
        {promoted ? (
          <>
            <span className="text-[12px] text-ink-muted">
              {t('myListings.promoEndsIn', 'Ends in')} {promoDaysLeft} {t('myListings.promoDaysLong', 'days')} ·{' '}
              {t('myListings.promoAutoStop', 'stops automatically, no auto-renew')}
            </span>
            <span className="flex-1" />
            <button type="button" onClick={deactivate} className="text-[12px] font-semibold text-ink-muted hover:text-ink">
              {t('myListings.promoEnd', 'End early')}
            </button>
          </>
        ) : (
          <>
            <div>
              <span className="font-mono text-[18px] font-bold text-primary">299 Kč</span>
              <span className="text-[12px] text-ink-muted">
                {' '}
                / {t('myListings.promoWeek', 'week')} · {t('myListings.promoDemo', 'demo, no charge')}
              </span>
            </div>
            <span className="flex-1" />
            <button
              type="button"
              onClick={activate}
              className={cn(pillBtnBase, 'bg-primary px-[20px] py-[10px] text-[13.5px] font-bold text-white shadow-e1')}
            >
              <Rocket size={15} /> {t('myListings.promoActivate', 'Promote for 1 week')}
            </button>
          </>
        )}
      </div>
    </RModal>
  )
}
